export const NODE_TYPES = {
  trigger: 'Trigger', delay: 'Time Delay', filter: 'Filter', sms: 'SMS Action', email: 'Email Action',
  if_else: 'If / Else', split_test: 'Split-test', exit_condition: 'Exit Condition', add_to_segment: 'Add to Segment', exit: 'Exit'
};
export const NODE_WIDTH = 230;
export const id = () => window.crypto.randomUUID();
export const emptyConditions = () => ({kind: 'group', operator: 'and', children: []});
export const editableConditions = (tree) => !tree?.kind ? emptyConditions() : tree.kind === 'group' ? tree : {...emptyConditions(), children: [tree]};
export function createNode(type, position) {
  const defaults = {
    trigger: {kind: 'event', event_type_id: null, conditions: {}}, delay: {value: 1, unit: 'days'},
    filter: {conditions: emptyConditions()}, sms: {template_id: null}, email: {template_id: null, sender_id: null},
    if_else: {conditions: emptyConditions()}, exit_condition: {conditions: emptyConditions()},
    split_test: {branches: [{id: id(), label: 'A', weight: 50}, {id: id(), label: 'B', weight: 50}]},
    add_to_segment: {segment_id: null}, exit: {}
  };
  return {id: id(), type, position, config: defaults[type]};
}
export function outputs(node) {
  if (node.type === 'exit') return [];
  if (['if_else', 'exit_condition'].includes(node.type)) return [{id: 'yes', label: 'YES'}, {id: 'no', label: 'NO'}];
  if (node.type === 'split_test') return (node.config.branches || []).map((branch) => ({id: branch.id, label: `${branch.label} · ${branch.weight}%`}));
  return [{id: 'next', label: 'Next'}];
}
export const nodeHeight = (node) => 104 + outputs(node).length * 26;
export const portY = (node, port) => 105 + Math.max(0, outputs(node).findIndex((output) => output.id === port)) * 26;
export const nodeName = (node) => node.label || NODE_TYPES[node.type];
export function fragment(graph, ids) {
  return {nodes: graph.nodes.filter((node) => ids.includes(node.id)),
    edges: graph.edges.filter((edge) => ids.includes(edge.source) && ids.includes(edge.target))};
}
export function insertFragment(graph, part, offset = {x: 40, y: 40}) {
  const mapping = Object.fromEntries(part.nodes.map((node) => [node.id, id()]));
  const added = part.nodes.map((node) => {
    const copy = {...JSON.parse(JSON.stringify(node)), id: mapping[node.id],
      position: {x: node.position.x + offset.x, y: node.position.y + offset.y}};
    if (copy.config.start_after_node_id) copy.config.start_after_node_id = mapping[copy.config.start_after_node_id] || copy.config.start_after_node_id;
    return copy;
  });
  const edges = part.edges.map((edge) => ({...edge, id: id(), source: mapping[edge.source], target: mapping[edge.target]}));
  return {graph: {nodes: [...graph.nodes, ...added], edges: [...graph.edges, ...edges]}, ids: added.map((node) => node.id)};
}
export function removeNodes(graph, ids) {
  return {nodes: graph.nodes.filter((node) => !ids.includes(node.id)),
    edges: graph.edges.filter((edge) => !ids.includes(edge.source) && !ids.includes(edge.target))};
}
export function connect(graph, source, port, target) {
  const remaining = graph.edges.filter((edge) => edge.source !== source || edge.port !== port);
  return {...graph, edges: target ? [...remaining, {id: id(), source, port, target}] : remaining};
}
export function fitView(graph, width, height) {
  if (!graph.nodes.length) return {x: 40, y: 60, zoom: 1};
  const left = Math.min(...graph.nodes.map((node) => node.position.x));
  const top = Math.min(...graph.nodes.map((node) => node.position.y));
  const right = Math.max(...graph.nodes.map((node) => node.position.x + NODE_WIDTH));
  const bottom = Math.max(...graph.nodes.map((node) => node.position.y + nodeHeight(node)));
  const zoom = Math.max(.001, Math.min(1, (width - 80) / (right - left), (height - 100) / (bottom - top)));
  return {zoom, x: (width - (right - left) * zoom) / 2 - left * zoom, y: (height - (bottom - top) * zoom) / 2 - top * zoom};
}

export function arrangeGraph(graph) {
  const ranks = Object.fromEntries(graph.nodes.map((node) => [node.id,0]));
  const incoming = Object.fromEntries(graph.nodes.map((node) => [node.id,graph.edges.filter((edge) => edge.target === node.id).length]));
  const queue = graph.nodes.filter((node) => incoming[node.id] === 0).map((node) => node.id);
  while (queue.length) {
    const current = queue.shift();
    graph.edges.filter((edge) => edge.source === current).forEach((edge) => {
      ranks[edge.target] = Math.max(ranks[edge.target],ranks[current]+1);
      incoming[edge.target] -= 1;
      if (incoming[edge.target] === 0) queue.push(edge.target);
    });
  }
  const columns = {};
  graph.nodes.forEach((node) => {
    if (!columns[ranks[node.id]]) columns[ranks[node.id]] = [];
    columns[ranks[node.id]].push(node);
  });
  const largest = Math.max(1,...Object.values(columns).map((column) => column.length));
  return {...graph,nodes:graph.nodes.map((node) => {
    const column = columns[ranks[node.id]], row = column.indexOf(node);
    return {...node,position:{x:60+ranks[node.id]*310,y:80+(largest-column.length)*110+row*220}};
  })};
}
